import { ZodError } from 'zod';

/**
 * Options for creating an ApiError
 */
export interface ApiErrorOptions {
  cause?: unknown;
  status?: number;
  statusText?: string;
  url?: string;
  responseData?: unknown;
}

/**
 * Custom error class for API errors.
 * Created by the hey-api error interceptor for HTTP 4xx/5xx responses and network failures.
 */
export class ApiError extends Error {
  readonly status?: number;
  readonly statusText?: string;
  readonly url?: string;
  readonly responseData?: unknown;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message, { cause: options.cause });
    this.name = 'ApiError';
    this.status = options.status;
    this.statusText = options.statusText;
    this.url = options.url;
    this.responseData = options.responseData;
  }
}

/**
 * Known business-level error codes returned by the API on the response body
 * as `{ "error_code": "..." }`.
 */
export const ORDER_ERROR_CODES = {
  PATIENT_HAS_EXISTING_ORDER: 'patient_exists_in_emr',
} as const;

/**
 * Returns the `error_code` string from an `ApiError`'s response body, or
 * `undefined` if the error isn't an `ApiError` or doesn't carry one.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.responseData;
  if (data && typeof data === 'object' && 'error_code' in data) {
    const code = (data as { error_code: unknown }).error_code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/**
 * Extracts logging properties from an error based on its type.
 * Used internally by the logger - not exported for external use.
 *
 * @param error - The error to extract properties from
 * @returns Record of properties for logging
 */
export function extractErrorProperties(error: unknown): Record<string, unknown> {
  const props: Record<string, unknown> = {};

  if (error instanceof ApiError) {
    props.errorType = 'api';
    props.status = error.status;
    props.statusText = error.statusText;
    props.url = error.url;
  } else if (error instanceof ZodError) {
    props.errorType = 'validation';
    props.zodIssues = error.issues;
  } else if (error instanceof Error) {
    props.errorType = 'application';
  } else {
    props.errorType = 'unknown';
  }

  return props;
}

/**
 * Normalizes any value to an Error instance.
 * Ensures PostHog always receives a proper Error with stack trace.
 *
 * @param error - The value to normalize
 * @returns An Error instance
 */
export function normalizeToError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  // For objects or other types, create an Error with the value as cause
  return new Error('Unknown error', { cause: error });
}
